import React, { useEffect, useState } from 'react';
import API from '../api';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './styles.css';

function OrderTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [trackingHistory, setTrackingHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetails();
    fetchTrackingHistory();
  }, [id]);

  const fetchOrderDetails = () => {
    API.get(`orders/${id}/`).then(res => {
      setOrder(res.data);
      setLoading(false);
    }).catch(err => {
      console.error('Error fetching order:', err);
      setLoading(false);
    });
  };

  const fetchTrackingHistory = () => {
    API.get(`orders/${id}/tracking/`).then(res => {
      setTrackingHistory(res.data);
    }).catch(err => {
      console.error('Error fetching tracking history:', err);
    });
  };

  const updateOrderStatus = (newStatus) => {
    const data = {
      status: newStatus,
      latitude: null, // This would come from GPS in a real app
      longitude: null,
      notes: ''
    };

    API.post(`orders/${id}/update_status/`, data).then(() => {
      fetchOrderDetails();
      fetchTrackingHistory();
    }).catch(err => {
      console.error('Error updating status:', err);
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#ffc107',
      'confirmed': '#17a2b8',
      'assigned': '#007bff',
      'picked_up': '#28a745',
      'in_transit': '#fd7e14',
      'delivered': '#28a745',
      'cancelled': '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  if (loading) {
    return <div className="page-container">{t('Loading')}...</div>;
  }

  if (!order) {
    return <div className="page-container">{t('Order not found')}</div>;
  }

  return (
    <div className="page-container">
      <div className="login-buttons">
        <button onClick={() => navigate('/orders')}>← {t('Back to Orders')}</button>
        <button onClick={() => navigate('/')}>🏠 {t('Home')}</button>
      </div>

      <h2>{t('Order Tracking')} - {order.order_number}</h2>

      {/* Order Details */}
      <div style={{border: '1px solid #ddd', padding: '20px', marginBottom: '20px', borderRadius: '5px'}}>
        <h3>{t('Order Details')}</h3>
        <div className="responsive-grid" style={{gap: '10px'}}>
          <div><strong>{t('Customer')}:</strong> {order.customer_name} ({order.customer_code})</div>
          <div><strong>{t('Driver')}:</strong> {order.driver_name || t('No driver')}</div>
          <div><strong>{t('Status')}:</strong> 
            <span style={{
              color: 'white',
              backgroundColor: getStatusColor(order.status),
              padding: '2px 8px',
              borderRadius: '3px',
              marginLeft: '5px'
            }}>
              {t(order.status)}
            </span>
          </div>
          <div><strong>{t('Priority')}:</strong> {t(order.priority)}</div>
          <div><strong>{t('Pickup Address')}:</strong> {order.pickup_address}</div>
          <div><strong>{t('Delivery Address')}:</strong> {order.delivery_address}</div>
          {order.barcode && <div><strong>{t('Barcode')}:</strong> {order.barcode}</div>}
          {order.tracking_url && <div><strong>{t('Tracking URL')}:</strong> 
            <a href={order.tracking_url} target="_blank" rel="noopener noreferrer">
              {t('View Tracking')}
            </a>
          </div>}
        </div>
      </div>

      {/* Status Update Buttons */}
      <div style={{marginBottom: '20px'}}>
        <h3>{t('Update Status')}</h3>
        <div className="flex-mobile-column" style={{gap: '10px'}}>
          {order.status === 'pending' && (
            <button onClick={() => updateOrderStatus('confirmed')} 
                    style={{backgroundColor: '#17a2b8', color: 'white'}}>
              {t('Confirm')}
            </button>
          )}
          {order.status === 'confirmed' && (
            <button onClick={() => updateOrderStatus('assigned')} 
                    style={{backgroundColor: '#007bff', color: 'white'}}>
              {t('Assign Driver')}
            </button>
          )}
          {order.status === 'assigned' && (
            <button onClick={() => updateOrderStatus('picked_up')} 
                    style={{backgroundColor: '#28a745', color: 'white'}}>
              {t('Mark as Picked Up')}
            </button>
          )}
          {order.status === 'picked_up' && (
            <button onClick={() => updateOrderStatus('in_transit')} 
                    style={{backgroundColor: '#fd7e14', color: 'white'}}>
              {t('Mark as In Transit')}
            </button>
          )}
          {order.status === 'in_transit' && (
            <button onClick={() => updateOrderStatus('delivered')} 
                    style={{backgroundColor: '#28a745', color: 'white'}}>
              {t('Mark as Delivered')}
            </button>
          )}
          {order.status !== 'cancelled' && order.status !== 'delivered' && (
            <button onClick={() => updateOrderStatus('cancelled')} 
                    style={{backgroundColor: '#dc3545', color: 'white'}}>
              {t('Cancel Order')}
            </button>
          )}
        </div>
      </div>

      {/* Tracking History */}
      <div>
        <h3>{t('Tracking History')}</h3>
        {trackingHistory.length === 0 ? (
          <p>{t('No tracking information available')}</p>
        ) : (
          <div className="flex-mobile-column" style={{gap: '10px'}}>
            {trackingHistory.map((track, index) => (
              <div key={index} style={{
                border: '1px solid #ddd',
                padding: '15px',
                borderRadius: '5px',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <strong style={{color: getStatusColor(track.status)}}>
                      {t(track.status)}
                    </strong>
                    <div style={{fontSize: '0.9em', color: '#666'}}>
                      {new Date(track.created_at).toLocaleString()}
                    </div>
                  </div>
                  {track.location_latitude && track.location_longitude && (
                    <div style={{fontSize: '0.8em', color: '#666'}}>
                      📍 {track.location_latitude}, {track.location_longitude}
                    </div>
                  )}
                </div>
                {track.notes && (
                  <div style={{marginTop: '5px', fontStyle: 'italic'}}>
                    {track.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Map Placeholder */}
      <div style={{marginTop: '20px'}}>
        <h3>{t('Location Map')}</h3>
        <div style={{
          height: '300px',
          backgroundColor: '#f0f0f0',
          border: '1px solid #ddd',
          borderRadius: '5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666'
        }}>
          {t('Map integration would be implemented here with Google Maps or similar service')}
        </div>
      </div>
    </div>
  );
}

export default OrderTracking;
